<template>
  <v-form ref="form" v-model="valid">
    <v-row dense>
      <v-col cols="12" md="6">
        <v-select
          v-model="formData.asset"
          :items="assetList"
          :item-text="formatAssetLabel"
          item-value="id"
          outlined
          dense
          :rules="[v => !!v || 'Vui lòng chọn thiết bị']"
          @change="onAssetChanged"
        >
          <template v-slot:label>
            Thiết bị gặp sự cố <span class="red--text font-weight-bold">*</span>
          </template>
        </v-select>
      </v-col>

      <!-- Số lượng hỏng -->
      <v-col cols="12" md="6">
        <v-text-field
          v-model.number="formData.brokenQuantity"
          type="number"
          min="1"
          :max="maxBrokenQty"
          :hint="`Tối đa ${maxBrokenQty} ${selectedAssetUnit}`"
          persistent-hint
          outlined
          dense
          :rules="[
            v => !!v || 'Vui lòng nhập số lượng',
            v => v > 0 || 'Số lượng phải lớn hơn 0',
            v => v <= maxBrokenQty || `Không được vượt quá ${maxBrokenQty}`
          ]"
        >
          <template v-slot:label>
            Số lượng hỏng <span class="red--text font-weight-bold">*</span>
          </template>
        </v-text-field>
      </v-col>

      <v-col cols="12" md="6">
        <v-text-field
          v-model="formData.reporterName"
          outlined
          dense
          :readonly="isTeacher"
          :rules="[v => !!v || 'Vui lòng nhập tên người báo']"
        >
          <template v-slot:label>
            Người phát hiện / Báo hỏng <span class="red--text font-weight-bold">*</span>
          </template>
        </v-text-field>
      </v-col>

      <v-col cols="12" md="6">
        <v-select
          v-model="formData.priority"
          :items="priorityList"
          outlined
          dense
        >
          <template v-slot:label>
            Mức độ ưu tiên <span class="red--text font-weight-bold">*</span>
          </template>
        </v-select>
      </v-col>

      <v-col cols="12">
        <v-textarea
          v-model="formData.issueDescription"
          placeholder="VD: Bị chập nguồn không lên điện, hỏng bóng đèn, gãy chân bàn ghế..."
          outlined
          dense
          rows="3"
          :rules="[v => !!v || 'Vui lòng mô tả sự cố']"
        >
          <template v-slot:label>
            Mô tả hiện tượng / Sự cố hư hỏng <span class="red--text font-weight-bold">*</span>
          </template>
        </v-textarea>
      </v-col>

      <!-- Các trường chỉ hiển thị cho Quản trị viên -->
      <template v-if="!isTeacher">
        <v-col cols="12" md="6">
          <v-select
            v-model="formData.status"
            :items="statusList"
            outlined
            dense
          >
            <template v-slot:label>
              Trạng thái xử lý <span class="red--text font-weight-bold">*</span>
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="formData.repairedBy"
            label="Đơn vị / Người phụ trách sửa chữa"
            placeholder="VD: Kỹ thuật viên IT, Đơn vị bảo hành ABC..."
            outlined
            dense
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model.number="formData.repairCost"
            type="number"
            min="0"
            label="Chi phí sửa chữa dự kiến (VNĐ)"
            outlined
            dense
          />
        </v-col>
        <v-col cols="12">
          <v-textarea
            v-model="formData.repairAction"
            label="Phương án khắc phục / Linh kiện thay thế"
            placeholder="VD: Thay bóng đèn máy chiếu, thay quạt tản nhiệt..."
            outlined
            dense
            rows="2"
          />
        </v-col>
      </template>
    </v-row>
  </v-form>
</template>

<script>
import { mapGetters } from 'vuex'
import { Asset } from '@/plugins/api'

export default {
  name: 'RepairForm',
  data() {
    return {
      valid: true,
      assetList: [],
      maxBrokenQty: 1,
      selectedAssetUnit: 'chiếc',
      priorityList: [
        { text: 'Thấp', value: 'LOW' },
        { text: 'Bình thường', value: 'MEDIUM' },
        { text: 'Cao', value: 'HIGH' },
        { text: 'Khẩn cấp', value: 'URGENT' }
      ],
      statusList: [
        { text: 'Chờ tiếp nhận', value: 'PENDING' },
        { text: 'Đang sửa chữa', value: 'IN_PROGRESS' },
        { text: 'Đã hoàn thành', value: 'COMPLETED' },
        { text: 'Đã hủy', value: 'CANCELLED' }
      ],
      formData: {
        asset: null,
        brokenQuantity: 1,
        reporterName: '',
        priority: 'MEDIUM',
        status: 'PENDING',
        issueDescription: '',
        repairedBy: '',
        repairCost: 0,
        repairAction: ''
      }
    }
  },
  computed: {
    ...mapGetters('auth', ['role', 'profile']),
    isTeacher() {
      return this.role && this.role.name === 'Teacher'
    }
  },
  async created() {
    if (this.profile && this.profile.name) {
      this.formData.reporterName = this.profile.name
    }
    try {
      const assets = await Asset.search({ _limit: -1, isDelete: false })
      this.assetList = assets || []
    } catch (e) {
      console.error(e)
    }
  },
  methods: {
    formatAssetLabel(a) {
      return `${a.name} (SL: ${a.quantity}, Khả dụng: ${a.availableQuantity} ${a.unit || ''})`
    },
    onAssetChanged(assetId) {
      const found = this.assetList.find(a => a.id === assetId)
      if (found) {
        this.maxBrokenQty = found.availableQuantity || found.quantity || 1
        this.selectedAssetUnit = found.unit || 'chiếc'
        this.formData.brokenQuantity = 1
      }
    },
    validate() {
      return this.$refs.form.validate()
    },
    getData() {
      return { ...this.formData }
    },
    resetValidation() {
      if (this.$refs.form) this.$refs.form.resetValidation()
    }
  }
}
</script>
